import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Card, SectionLabel, EmptyState } from "../../components/customer-app/ui";
import {
  Sun, MapPin, Mail, Phone, User as UserIcon, ChevronRight,
  ShieldCheck, ShieldAlert, Cpu, Hash, Calendar, Loader2, PartyPopper, Sparkles,
} from "lucide-react";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FAF8F5] relative overflow-hidden" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
      <div className="pointer-events-none absolute -top-28 -right-20 w-72 h-72 rounded-full bg-[#FEF3C7] blur-3xl opacity-70" />
      <div className="pointer-events-none absolute top-1/2 -left-28 w-72 h-72 rounded-full bg-[#DCFCE7] blur-3xl opacity-70" />
      <div className="relative max-w-md mx-auto min-h-screen flex flex-col px-6 pt-10 pb-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] flex items-center justify-center shadow-[0_4px_14px_0_rgba(13,71,33,0.25)]">
            <Sun className="w-6 h-6 text-amber-300" strokeWidth={2} />
          </div>
          <div className="text-lg font-extrabold tracking-tight text-[#0F172A]">Purvodaya Solar</div>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function CustomerOnboarding() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(null); // confirm | installations | basic | ask | thanks
  const [customers, setCustomers] = useState([]);
  const [addrIdx, setAddrIdx] = useState(0);
  const [ibs, setIbs] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/customer-app/onboarding/status").then(({ data }) => {
      if (!data.needs_onboarding) { nav("/app", { replace: true }); return; }
      setCustomers(data.customers || []);
      setPhase(data.has_master_data ? "confirm" : "basic");
    }).catch(() => nav("/app", { replace: true })).finally(() => setLoading(false));
  }, [nav]);

  async function proceedToInstallations() {
    setLoading(true);
    try {
      const { data } = await api.get("/customer-app/installed-base");
      setIbs(data);
      setPhase("installations");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }

  async function finishOnboarding(navTo = "/app") {
    try { await api.post("/customer-app/onboarding/complete"); } catch { /* non-blocking */ }
    nav(navTo, { replace: true });
  }

  async function submitBasicDetails(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/customer-app/onboarding/create-profile", { name, email: email || undefined, address, pin_code: pinCode || undefined });
      setPhase("ask");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  if (loading && !phase) {
    return <Shell><div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0D4721]" /></div></Shell>;
  }

  const current = customers[addrIdx] || customers[0];

  return (
    <Shell>
      {phase === "confirm" && current && (
        <div data-testid="onboarding-confirm" className="flex-1 flex flex-col">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0F172A] leading-tight">Welcome back!</h1>
          <p className="mt-2 text-sm text-slate-600">We found your details on file. Please confirm.</p>

          {customers.length > 1 && (
            <div className="flex flex-wrap gap-2 mt-5" data-testid="onboarding-address-breadcrumbs">
              {customers.map((c, i) => (
                <button key={c.id} onClick={() => setAddrIdx(i)} data-testid={`onboarding-address-chip-${i}`}
                  className={"px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors " + (i === addrIdx ? "border-[#0D4721] bg-emerald-50 text-[#0D4721]" : "border-slate-200 text-slate-500 bg-white")}>
                  Address {i + 1}
                </button>
              ))}
            </div>
          )}

          <Card className="p-5 mt-5 space-y-4">
            <div className="flex items-start gap-3">
              <UserIcon className="w-4 h-4 text-[#0D4721] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Name</div>
                <div className="text-base font-bold text-slate-900" data-testid="onboarding-confirm-name">{current.customer_name || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-[#0D4721] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Phone Number</div>
                <div className="text-base font-semibold text-slate-800 font-mono" data-testid="onboarding-confirm-phone">{current.phone || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-[#0D4721] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Email ID</div>
                <div className="text-base font-semibold text-slate-800" data-testid="onboarding-confirm-email">{current.email || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#0D4721] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Address</div>
                <div className="text-base font-semibold text-slate-800 leading-snug" data-testid="onboarding-confirm-address">{current.address || "—"}</div>
              </div>
            </div>
          </Card>

          <div className="flex-1" />
          <button onClick={proceedToInstallations} disabled={loading} data-testid="onboarding-confirm-continue"
            className="w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-4 rounded-xl text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2.5 disabled:opacity-40 mt-6">
            {loading ? "Loading…" : "Looks good, continue"} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {phase === "installations" && (
        <div data-testid="onboarding-installations" className="flex-1 flex flex-col">
          <h1 className="text-2xl font-extrabold tracking-tight text-[#0F172A]">Your Installations</h1>
          <p className="mt-1 text-sm text-slate-600">Here's everything on record for you.</p>

          <div className="mt-5 space-y-3 max-h-[58vh] overflow-y-auto pr-1" data-testid="onboarding-ib-scroll">
            {ibs && ibs.length === 0 && (
              <EmptyState icon={Sun} title="No installations yet" subtitle="Once your system is commissioned, it'll show up here." />
            )}
            {ibs && ibs.map((ib) => (
              <Card key={ib.id} className="p-4" data-testid={`onboarding-ib-card-${ib.ib_number}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{ib.ib_number}</div>
                  <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold " + (ib.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700")}>
                    {ib.is_active ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                    {ib.is_active ? "In Warranty" : "Out of Warranty"}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-2">
                  <Calendar className="w-3.5 h-3.5" /> Installed {ib.commissioning_date ? new Date(ib.commissioning_date).toLocaleDateString() : "—"}
                </div>
                <div className="mt-3 space-y-1.5">
                  {(ib.equipment || []).map((eq) => (
                    <div key={eq.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-2.5 py-1.5">
                      <div className="flex items-center gap-1.5 text-slate-700 font-medium truncate">
                        <Cpu className="w-3 h-3 text-slate-400 flex-shrink-0" /> {eq.material_name}
                      </div>
                      <div className="flex items-center gap-2 text-slate-500 font-mono text-[10.5px] flex-shrink-0">
                        <span className="inline-flex items-center gap-0.5"><Hash className="w-2.5 h-2.5" />{eq.serial_code}</span>
                        <span>Qty {eq.quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          <div className="flex-1" />
          <button onClick={() => finishOnboarding("/app")} data-testid="onboarding-installations-continue"
            className="w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-4 rounded-xl text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2.5 mt-6">
            Continue to App <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {phase === "basic" && (
        <div data-testid="onboarding-basic-details" className="flex-1 flex flex-col">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0F172A] leading-tight">Let's get you set up</h1>
          <p className="mt-2 text-sm text-slate-600">We couldn't find any existing records for this number. Tell us a bit about you.</p>

          <form onSubmit={submitBasicDetails} className="space-y-4 mt-6">
            <div>
              <SectionLabel className="mb-2">Full name</SectionLabel>
              <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="onboarding-basic-name"
                placeholder="Your full name"
                className="w-full bg-white rounded-xl border-2 border-slate-200 focus:border-[#F59E0B] outline-none px-4 py-3.5 text-base text-[#0F172A] placeholder-slate-300" />
            </div>
            <div>
              <SectionLabel className="mb-2">Email ID <span className="text-slate-400">(optional)</span></SectionLabel>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="onboarding-basic-email"
                placeholder="you@example.com"
                className="w-full bg-white rounded-xl border-2 border-slate-200 focus:border-[#F59E0B] outline-none px-4 py-3.5 text-base text-[#0F172A] placeholder-slate-300" />
            </div>
            <div>
              <SectionLabel className="mb-2">Address</SectionLabel>
              <textarea value={address} onChange={(e) => setAddress(e.target.value)} required rows={3} data-testid="onboarding-basic-address"
                placeholder="Your current address"
                className="w-full bg-white rounded-xl border-2 border-slate-200 focus:border-[#F59E0B] outline-none px-4 py-3.5 text-base text-[#0F172A] placeholder-slate-300" />
            </div>
            <div>
              <SectionLabel className="mb-2">PIN Code <span className="text-slate-400">(optional)</span></SectionLabel>
              <input inputMode="numeric" maxLength={6} value={pinCode} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="onboarding-basic-pin"
                placeholder="560001"
                className="w-full bg-white rounded-xl border-2 border-slate-200 focus:border-[#F59E0B] outline-none px-4 py-3.5 text-base text-[#0F172A] placeholder-slate-300" />
            </div>
            <button type="submit" disabled={saving} data-testid="onboarding-basic-continue"
              className="w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-4 rounded-xl text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2.5 disabled:opacity-40">
              {saving ? "Saving…" : "Continue"} <ChevronRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {phase === "ask" && (
        <div data-testid="onboarding-ask-install" className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#0F172A]">Looking for a new installation?</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-xs">Let us know and our solar experts will get in touch.</p>
          <div className="grid grid-cols-2 gap-3 w-full mt-8">
            <button onClick={() => finishOnboarding("/app/buy-installation")} data-testid="onboarding-ask-yes"
              className="py-4 rounded-xl text-sm uppercase tracking-wider font-bold bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white">
              Yes, please
            </button>
            <button onClick={() => setPhase("thanks")} data-testid="onboarding-ask-no"
              className="py-4 rounded-xl text-sm uppercase tracking-wider font-bold border-2 border-slate-200 text-slate-600">
              Not right now
            </button>
          </div>
        </div>
      )}

      {phase === "thanks" && (
        <div data-testid="onboarding-thanks" className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <PartyPopper className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#0F172A]">You're all set!</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-xs">Thank you — our team is here whenever you need support. Enjoy the app!</p>
          <button onClick={() => finishOnboarding("/app")} data-testid="onboarding-thanks-continue"
            className="mt-8 w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-4 rounded-xl text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2.5">
            Continue to App <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </Shell>
  );
}
